import memoize from "memoizee";
import {
  getRoleplay,
  createRoleplay,
} from "@nualang/nualang-api-and-queries/APIs/Roleplays";
import { memoizeGetUserDetails } from "./users";
import {
  DataType,
  invalidateCache,
} from "@nualang/nualang-api-and-queries/APIs/invalidation";
import { getImageUrl, getImageUrlWithSize } from "../imageDownload";
import { UploadRoleplayImage } from "../imageUpload";
import config from "../../config";

export const memoizeGetRoleplay = memoize(getRoleplay, { promise: true });

export const formatRoleplay = async (r, username) => {
  const userDetails = await memoizeGetUserDetails(r.createdBy);
  const userImage =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.picture
      : null;
  const verificationStatus =
    userDetails && userDetails.verificationStatus
      ? userDetails.verificationStatus
      : null;
  const gameAward =
    userDetails && userDetails.gameAward ? userDetails.gameAward : null;
  // eslint-disable-next-line camelcase
  const preferred_username =
    userDetails && userDetails.UserAttributes
      ? userDetails.UserAttributes.preferred_username
      : null;
  const isCreator = r.createdBy === username;
  let pictureXs = null;
  let cardPicture = null;
  if (r.pictureS3Key) {
    pictureXs = getImageUrl(r.pictureS3Key, {
      resize: {
        width: 25,
      },
    });
    cardPicture = getImageUrl(r.pictureS3Key, {
      resize: {
        width: 600,
      },
    });
  } else if (
    r.picture &&
    r.picture.includes("nualang.com/") &&
    r.picture.includes("images")
  ) {
    pictureXs = getImageUrlWithSize(r.picture, null, 25);
    cardPicture = getImageUrlWithSize(r.picture, null, 600);
  }

  if (r.picture && r.picture.includes(config.OldImageCreatorURL)) {
    const path = r.picture.split(config.OldImageCreatorURL)[1];
    r.picture = `${config.API}/image-creator${path}`;
  }

  return {
    ...r,
    userImage,
    verificationStatus,
    gameAward,
    createdByName: preferred_username,
    isCreator,
    pictureXs,
    cardPicture,
  };
};

export const formatRoleplays = (roleplays = [], username) =>
  Promise.all(roleplays.map((r) => formatRoleplay(r, username)));

export const formatCompletedRoleplay = async (completion, username) => {
  const { roleplayId } = completion;
  const roleplayResponse = await memoizeGetRoleplay(roleplayId);
  if (roleplayResponse.Item) {
    const formattedRoleplay = await formatRoleplay(
      roleplayResponse.Item,
      username,
    );
    return formattedRoleplay;
  }

  return null;
};

export const formatCompletedRoleplays = async (completions = [], username) =>
  Promise.all(completions.map((c) => formatCompletedRoleplay(c, username)));

export const invalidateRoleplayCache = async (roleplay) => {
  if (roleplay.courseSectionTopicId.includes("none")) {
    await invalidateCache(DataType.ROLEPLAY, {
      roleplayId: roleplay.roleplayId,
      learnLang: roleplay.learnLang,
    });
  } else {
    const [courseId, sectionId, topicId] =
      roleplay.courseSectionTopicId.split("|");
    await invalidateCache(DataType.TOPIC_ROLEPLAY, {
      courseId,
      sectionId,
      topicId,
      roleplayId: roleplay.roleplayId,
      learnLang: roleplay.learnLang,
    });
  }
};

export const duplicateRoleplay = async (
  values,
  courseId,
  sectionId,
  topicId,
) => {
  let newRoleplay = {};
  let pictureBase64String = "";
  let actorOnePictureBase64String = "";
  let actorTwoPictureBase64String = "";

  const newRoleplayValues = {
    ...values,
    roleplayName: values.roleplayName + " (duplicate)",
    username: "",
    userImage: "",
    isCreator: true,
    visibility: "private",
  };
  delete newRoleplayValues.createdBy;
  delete newRoleplayValues.createdByName;
  delete newRoleplayValues.createdAt;
  delete newRoleplayValues.courseSectionTopicId;
  delete newRoleplayValues.roleplayId;
  delete newRoleplayValues.exerciseCompletionTotal;
  delete newRoleplayValues.exerciseStartTotal;

  if (newRoleplayValues.picture) {
    pictureBase64String = newRoleplayValues.picture.split(",");
  }
  if (newRoleplayValues.actor1.picture) {
    actorOnePictureBase64String = newRoleplayValues.actor1.picture.split(",");
  }
  if (newRoleplayValues.actor2.picture) {
    actorTwoPictureBase64String = newRoleplayValues.actor2.picture.split(",");
  }
  newRoleplayValues.actor1.picture = "";
  newRoleplayValues.actor2.picture = "";
  newRoleplayValues.picture = "";

  const isTopicExercise = courseId && sectionId && topicId ? true : false;
  if (isTopicExercise) {
    newRoleplayValues.courseSectionTopicId = `${courseId}|${sectionId}|${topicId}`;
  }
  const calls = ["", "", ""];
  if (pictureBase64String) {
    calls[0] = UploadRoleplayImage(
      pictureBase64String[1] || pictureBase64String[0],
      `${newRoleplay.roleplayId}-picture`,
    );
  }
  if (actorOnePictureBase64String) {
    calls[1] = UploadRoleplayImage(
      actorOnePictureBase64String[1] || actorOnePictureBase64String[0],
      `${newRoleplay.roleplayId}-actor1`,
    );
  }
  if (actorTwoPictureBase64String) {
    calls[2] = UploadRoleplayImage(
      actorTwoPictureBase64String[1] || actorTwoPictureBase64String[0],
      `${newRoleplay.roleplayId}-actor2`,
    );
  }

  const imageResponses = await Promise.all(calls);

  [newRoleplayValues.picture, newRoleplayValues.pictureS3Key] =
    imageResponses[0];
  [newRoleplayValues.actor1.picture, newRoleplayValues.actor1.pictureS3Key] =
    imageResponses[1];
  [newRoleplayValues.actor2.picture, newRoleplayValues.actor2.pictureS3Key] =
    imageResponses[2];

  newRoleplay = await createRoleplay(newRoleplayValues, isTopicExercise);

  return newRoleplay;
};
